/* import __COLOCATED_TEMPLATE__ from './common.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */

import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import View from 'embercom/objects/inbox/inboxes/view';
import type InboxState from 'embercom/services/inbox-state';
import type Session from 'embercom/services/session';
import type { InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

type Config = {
  title: string;
  description: string;
  cta: string;
  linkText: string;
  articleId: number;
  icon: InterfaceIconName;
};

export default class Inbox2LeftNavFoldersDefaultAiChatbotEmptyState extends Component {
  @service declare inboxState: InboxState;
  @service declare intercomEventService: $TSFixMe;
  @service declare router: RouterService;
  @service declare session: Session;

  get config(): Config {
    if (this.isResolvedInbox) {
      return {
        title: 'inbox.inbox-list.ai-chatbot.resolved.empty-state.title',
        description: 'inbox.inbox-list.ai-chatbot.resolved.empty-state.description',
        cta: 'inbox.inbox-list.ai-chatbot.resolved.empty-state.cta',
        linkText: 'inbox.inbox-list.ai-chatbot.resolved.empty-state.link-text',
        articleId: 8205718,
        icon: 'thumbsup',
      };
    }

    if (this.isRoutedInbox) {
      return {
        title: 'inbox.inbox-list.ai-chatbot.routed.empty-state.title',
        description: 'inbox.inbox-list.ai-chatbot.routed.empty-state.description',
        cta: 'inbox.inbox-list.ai-chatbot.routed.empty-state.cta',
        linkText: 'inbox.inbox-list.ai-chatbot.routed.empty-state.link-text',
        articleId: 7860256,
        icon: 'new-direct-message',
      };
    }

    if (this.isAbandonedInbox) {
      return {
        title: 'inbox.inbox-list.ai-chatbot.not_routed_or_resolved.empty-state.title',
        description: 'inbox.inbox-list.ai-chatbot.not_routed_or_resolved.empty-state.description',
        cta: 'inbox.inbox-list.ai-chatbot.not_routed_or_resolved.empty-state.cta',
        linkText: 'inbox.inbox-list.ai-chatbot.not_routed_or_resolved.empty-state.link-text',
        articleId: 7860256,
        icon: 'tooltip',
      };
    }

    return {
      title: 'inbox.inbox-list.ai-chatbot.all-conversations.empty-state.title',
      description: 'inbox.inbox-list.ai-chatbot.all-conversations.empty-state.description',
      cta: 'inbox.inbox-list.ai-chatbot.all-conversations.empty-state.cta',
      linkText: 'inbox.inbox-list.ai-chatbot.all-conversations.empty-state.link-text',
      articleId: 7860256,
      icon: 'fin',
    };
  }

  get isAllConversationsInbox() {
    return (
      this.inboxState.activeInbox instanceof View &&
      this.inboxState.activeInbox.viewSummary.systemType === 'ai_agent_all_conversations'
    );
  }

  get isResolvedInbox() {
    return (
      this.inboxState.activeInbox instanceof View &&
      this.inboxState.activeInbox.viewSummary.systemType === 'ai_agent_resolved'
    );
  }

  get isRoutedInbox() {
    return (
      this.inboxState.activeInbox instanceof View &&
      this.inboxState.activeInbox.viewSummary.systemType === 'ai_agent_routed'
    );
  }

  get isAbandonedInbox() {
    return (
      this.inboxState.activeInbox instanceof View &&
      this.inboxState.activeInbox.viewSummary.systemType === 'ai_agent_abandoned'
    );
  }

  get openedInbox() {
    if (this.inboxState.activeInbox instanceof View) {
      return this.inboxState.activeInbox.viewSummary.systemType;
    }

    return null;
  }

  @action
  openArticle(event: MouseEvent) {
    event.preventDefault();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: `empty_state_link_${this.openedInbox}`,
      place: 'inbox',
      section: 'inbox_conversations_ai_chatbot_empty_state',
    });
    window.Intercom('showArticle', this.config.articleId);
  }

  @action
  onCtaClick() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: `empty_state_cta_${this.openedInbox}`,
      place: 'inbox',
      section: 'inbox_conversations_ai_chatbot_empty_state',
    });

    this.router.transitionTo('apps.app.automation.fin-ai-agent.content', this.session.workspace.id);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::LeftNav::Folders::Default::AiChatbot::EmptyState::Common': typeof Inbox2LeftNavFoldersDefaultAiChatbotEmptyState;
  }
}
